import React, { useLayoutEffect } from "react";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { WorkspaceTodoReceiversQuery } from "./__generated__/WorkspaceTodoReceiversQuery.graphql";
import { ReceiverStoresSelector } from "../../../WorkspaceEntity/ReceiverStoresSelector";
import { useNavigationHeaderOptions } from "../../../../../common/navigationHeaderOptions";
import { useIntl } from "react-intl";

import { TopicSelector } from "../../../WorkspaceEntity/TopicSelector";

function InnerWorkspaceTodoReceivers(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoReceivers"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceTodoReceiversQuery>(
    graphql`
      query WorkspaceTodoReceiversQuery(
        $workspaceTodoId: ID!
        $workspaceId: ID!
      ) {
        workspace(id: $workspaceId) {
          topics {
            ...TopicSelector_orgTopic
          }
        }

        todoWorkspace(id: $workspaceTodoId) @required(action: THROW) {
          id
          status
          potentialReceiverStores {
            ...ReceiverStoresSelector_receiverStores
          }
          topic {
            id
            isPrivate
          }
          updatedAtDate {
            ...FormattedDate
            isoString
          }
          updatedBy {
            name
            id
          }
          receiverStores {
            id
          }
        }
      }
    `,
    {
      workspaceTodoId: props.route.params.workspaceTodoId,
      workspaceId: props.route.params.workspaceId,
    },
    {
      fetchPolicy: "store-or-network",
      fetchKey: props.fetchKey,
    }
  );

  const [setStoreIds] = useMutation(graphql`
    mutation WorkspaceTodoReceiversSetStoreIdsMutation(
      $id: ID!
      $storeIds: [ID!]
    ) @raw_response_type {
      todoWorkspace(id: $id) {
        updateReceiverStoreIds(storeIds: $storeIds) {
          id
          todoCount
          receiverStores {
            id
          }
          ...WorkspaceTodoEditMainEditedInfo_Todo
        }
      }
    }
  `);
  const [setTopicId] = useMutation(graphql`
    mutation WorkspaceTodoReceiversSetTopicIdMutation($id: ID!, $topicId: ID)
    @raw_response_type {
      todoWorkspace(id: $id) {
        updateReceiverTopicId(topicId: $topicId) {
          id
          potentialReceiverStores {
            ...ReceiverStoresSelectorListItem_receiverStore
          }
          topic {
            id
          }
          receiverStores {
            id
          }
        }
      }
    }
  `);
  const receiverStoreIds = data.todoWorkspace.receiverStores.map(
    (store) => store.id
  );

  const disabled = data.todoWorkspace.status !== "DRAFT";

  const onTopicSelect = (topicId: string | undefined) => {
    setTopicId({
      variables: { id: data.todoWorkspace.id, topicId },
      optimisticResponse: {
        todoWorkspace: {
          updateReceiverTopicId: {
            id: data.todoWorkspace.id,
            topic: { id: topicId },
          },
        },
      },
    });
  };

  const hasTopics =
    (data.workspace.topics?.length ?? 0) > 0 || !!data.todoWorkspace.topic;
  return (
    <>
      {hasTopics ? (
        <TopicSelector
          topics={data.workspace.topics ?? []}
          currentTopicId={data.todoWorkspace.topic?.id}
          onTopicSelect={onTopicSelect}
        />
      ) : null}
      <ReceiverStoresSelector
        disabled={disabled}
        selectedStoreIds={receiverStoreIds}
        stores={data.todoWorkspace?.potentialReceiverStores ?? []}
        isTopicPrivate={data.todoWorkspace.topic?.isPrivate ?? false}
        onStoreSetSelect={(newSelectedStoreIds) => {
          const optimisticNewIds = newSelectedStoreIds.map((id) => ({ id }));

          setStoreIds({
            variables: {
              id: props.route.params.workspaceTodoId,
              storeIds: newSelectedStoreIds,
            },
            optimisticResponse: {
              todoWorkspace: {
                updateReceiverStoreIds: {
                  id: props.route.params.workspaceTodoId,
                  todoCount: optimisticNewIds.length,
                  receiverStores: optimisticNewIds,
                  updatedAtDate: data.todoWorkspace.updatedAtDate
                    ? {
                        ...data.todoWorkspace.updatedAtDate,
                        __isDate: "UnzonedDate" as const,
                      }
                    : null,

                  updatedBy: data.todoWorkspace.updatedBy,
                },
              },
            },
          });
        }}
      />
    </>
  );
}

export const WorkspaceTodoReceivers = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"WorkspaceTodoReceivers">) => {
    const getOpt = useNavigationHeaderOptions();
    const intl = useIntl();
    useLayoutEffect(() => {
      props.navigation.setOptions(
        getOpt({
          type: "CLOSE",
          onClose: props.navigation.goBack,
          title: intl.formatMessage({
            defaultMessage: "Recipients",
            description: "Workspace todo edit recipients label",
          }),
        })
      );
    }, [getOpt, props, intl]);
    return (
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <InnerWorkspaceTodoReceivers fetchKey={fetchKey} {...props} />
        )}
      </LazyQueryNetworkBoundrary>
    );
  },
  true,
  {
    contentStyle: {
      width: 600,
      maxHeight: 600,
    },
  }
);
